import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Input, Radio, Select, Tabs, Table, Space, theme } from 'antd';
import { HomeIcon, SearchIcon, UserIcon, ChatIcon, CameraIcon, MenuIcon, XIcon, ChevronLeftIcon, DotsVerticalIcon } from '../components/icons';
import * as SolidIcons from '@heroicons/react/solid';
import { shadows as _shadows } from '../theme/tokens/shadows';
import { radius } from '../theme/tokens/radius';
import { typography } from '../theme/tokens/typography';
import { spacing } from '../theme/tokens/spacing';
import { colors } from '../theme/tokens/colors';
import FloatingPrimaryButton from '../components/FloatingPrimaryButton/FloatingPrimaryButton';
import AccentButton from '../components/AccentButton/AccentButton';
import OutlineButton from '../components/OutlineButton/OutlineButton';
import IconButton from '../components/IconButton/IconButton';
import SpecModal from '../components/SpecModal/SpecModal';
import SpecDrawer from '../components/SpecDrawer/SpecDrawer';
import SpecActionSheet, { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import SpecBottomSheet from '../components/SpecBottomSheet/SpecBottomSheet';
import UserTag from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { useHeader } from '../components/Layout/MainLayout';
import { ShowListing } from '../components/DirectoryListings/ShowListing/ShowListing';
import { BandListing } from '../components/DirectoryListings/BandListing/BandListing';
import { MusicianListing } from '../components/DirectoryListings/MusicianListing/MusicianListing';
import { VenueListing } from '../components/DirectoryListings/VenueListing/VenueListing';
import { AddListing } from '../components/DirectoryListings/AddListing/AddListing';
import { PlayButton } from '../components/PlayButton/PlayButton';
import { PlayButtonLarge } from '../components/PlayButton/PlayButtonLarge';
import { PlayButtonXL } from '../components/PlayButton/PlayButtonXL';
import { PhotoRibbon, PhotoGrid, PhotoGalleryDrawer } from '../components/PhotoGallery';
import { Tag, TagRow } from '../components/Tag';
import { Comment, CommentList, CommentDrawer, CommentReplyBar, AddCommentDrawer } from '../components/Comment';
import type { ShowContext } from '../components/Comment/AddCommentDrawer';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import AccentButtonDark from '../components/AccentButtonDark/AccentButtonDark';
import BrandedButton from '../components/BrandedButton/BrandedButton';
import { MarqueeText } from '../components/MarqueeText/MarqueeText';
import { DateSubheader } from '../components/DateSubheader';
import { DirectorySearch } from '../components/DirectorySearch/DirectorySearch';
import { ShowPhotoCard } from '../components/ShowPhotoCard/ShowPhotoCard';
import { BandInfoLockup } from '../components/BandInfoLockup/BandInfoLockup';
import { VenueCard } from '../components/VenueCard/VenueCard';
import { PoweredBySpec } from '../components/PoweredBySpec/PoweredBySpec';
import { PhotoOverlayInfo, PhotoThumbnailInfo } from '../components/PhotoGallery';
import { PhotoActionBar } from '../components/PhotoGallery/PhotoActionBar';
import { AddPhotoDrawer } from '../components/PhotoGallery/AddPhotoDrawer';
import AddToCalendarSheet from '../components/AddToCalendarSheet/AddToCalendarSheet';
import CalendarOverlay from '../components/CalendarOverlay/CalendarOverlay';
import { ShowContributorsDrawer } from '../components/ManageDrawer/ShowContributorsDrawer';
import { BandMembersDrawer } from '../components/ManageDrawer/BandMembersDrawer';
import './Show.css';

const HeaderConfigDemo: React.FC = () => {
  const { config, setConfig, resetConfig } = useHeader();

  return (
    <Space direction="vertical" size="small">
      <Space wrap>
        <Checkbox
          checked={config.showHamburger}
          onChange={(e) => setConfig({ showHamburger: e.target.checked })}
        >
          Hamburger
        </Checkbox>
        <Checkbox
          checked={config.showClose}
          onChange={(e) => setConfig({ showClose: e.target.checked })}
        >
          Close (X)
        </Checkbox>
        <Checkbox
          checked={config.showBack}
          onChange={(e) => setConfig({ showBack: e.target.checked })}
        >
          Back
        </Checkbox>
        <Checkbox
          checked={config.showLogo}
          onChange={(e) => setConfig({ showLogo: e.target.checked })}
        >
          Logo
        </Checkbox>
        <Checkbox
          checked={config.showSearch}
          onChange={(e) => setConfig({ showSearch: e.target.checked })}
        >
          Search Bar
        </Checkbox>
        <Checkbox
          checked={config.showMore}
          onChange={(e) => setConfig({ showMore: e.target.checked })}
        >
          More (...)
        </Checkbox>
        <Checkbox
          checked={config.showGradient}
          onChange={(e) => setConfig({ showGradient: e.target.checked })}
        >
          Header Gradient
        </Checkbox>
      </Space>
      <Button size="small" onClick={resetConfig}>Reset Header</Button>
    </Space>
  );
};

const PlayButtonDemo: React.FC = () => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [isPlayingLarge, setIsPlayingLarge] = React.useState(false);
  const [progressLarge, setProgressLarge] = React.useState(0);
  const [isPlayingXL, setIsPlayingXL] = React.useState(false);
  const [progressXL, setProgressXL] = React.useState(0);

  React.useEffect(() => {
    if (!isPlaying) return;

    const duration = 30000; // 30 seconds
    const interval = 100; // Update every 100ms
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          return 0; // Reset and loop
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [isPlaying]);

  React.useEffect(() => {
    if (!isPlayingLarge) return;

    const duration = 30000; // 30 seconds
    const interval = 100; // Update every 100ms
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgressLarge(prev => {
        const next = prev + increment;
        if (next >= 100) {
          return 0; // Reset and loop
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [isPlayingLarge]);

  React.useEffect(() => {
    if (!isPlayingXL) return;

    const duration = 30000; // 30 seconds
    const interval = 100; // Update every 100ms
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgressXL(prev => {
        const next = prev + increment;
        if (next >= 100) {
          return 0; // Reset and loop
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [isPlayingXL]);

  const handleClick = () => {
    setIsPlaying(!isPlaying);
  };

  const handleClickLarge = () => {
    setIsPlayingLarge(!isPlayingLarge);
  };

  const handleClickXL = () => {
    setIsPlayingXL(!isPlayingXL);
  };

  return (
    <Space direction="horizontal" size="large" align="start">
      <Space direction="vertical" size="middle">
        <div style={{ color: '#ABABAB' }}>
          36px button (standard)
        </div>
        <PlayButton
          size={36}
          isPlaying={isPlaying}
          progress={progress}
          onClick={handleClick}
        />
      </Space>
      <Space direction="vertical" size="middle">
        <div style={{ color: '#ABABAB' }}>
          52px button (large - for ShowCards)
        </div>
        <PlayButtonLarge
          size={52}
          isPlaying={isPlayingLarge}
          progress={progressLarge}
          onClick={handleClickLarge}
        />
      </Space>
      <Space direction="vertical" size="middle">
        <div style={{ color: '#ABABAB' }}>
          98px button (XL - for hero/featured)
        </div>
        <PlayButtonXL
          size={98}
          isPlaying={isPlayingXL}
          progress={progressXL}
          onClick={handleClickXL}
        />
      </Space>
    </Space>
  );
};

const shadows: any = _shadows;


const { useToken } = theme;
const { Option } = Select;

function SystemTest() {
  const { token } = useToken();
  const { setOnMoreClick, isAuthenticated, setIsAuthenticated } = useHeader();
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerNoOverlayOpen, setDrawerNoOverlayOpen] = useState(false);
  const [persistentDrawerOpen, setPersistentDrawerOpen] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState<string | number>(200);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showActionSheetOpen, setShowActionSheetOpen] = useState(false);
  const [customBottomSheetOpen, setCustomBottomSheetOpen] = useState(false);
  const [customBottomSheetWithTitleOpen, setCustomBottomSheetWithTitleOpen] = useState(false);
  const [customBottomSheetNoHandleOpen, setCustomBottomSheetNoHandleOpen] = useState(false);
  const [musicianInfoOpen, setMusicianInfoOpen] = useState(false);
  const [fanInfoOpen, setFanInfoOpen] = useState(false);
  const [bandInfoOpen, setBandInfoOpen] = useState(false);
  const [venueInfoOpen, setVenueInfoOpen] = useState(false);
  const [showContributorInfoOpen, setShowContributorInfoOpen] = useState(false);
  const [bandMemberInfoOpen, setBandMemberInfoOpen] = useState(false);
  const [noAccountInfoOpen, setNoAccountInfoOpen] = useState(false);
  const [photoGalleryDrawerOpen, setPhotoGalleryDrawerOpen] = useState(false);
  const [photoGalleryWithTabsOpen, setPhotoGalleryWithTabsOpen] = useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [addCommentDrawerOpen, setAddCommentDrawerOpen] = useState(false);
  const [addPhotoDrawerOpen, setAddPhotoDrawerOpen] = useState(false);
  const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);
  const [calendarOverlayOpen, setCalendarOverlayOpen] = useState(false);
  const [showContributorsOpen, setShowContributorsOpen] = useState(false);
  const [bandMembersOpen, setBandMembersOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Sample show context for drawers
  const sampleShowContext: ShowContext = {
    bandName: 'Johnny Mullenax Band',
    venueName: 'Mercury Lounge',
    date: 'Wed Sep 17',
    imageUrl: '/images/bands/johnny-mullenax.png',
  };

  // Sample photos for gallery demos
  const samplePhotos = [
    'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1508854710579-5cecc3a9ff17?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&h=400&fit=crop',
    'https://images.unsplash.com/photo-1574391884720-bbc3740c59d1?w=400&h=400&fit=crop',
  ];

  // Register the More button callback to open the action sheet
  useEffect(() => {
    setOnMoreClick(() => setShowActionSheetOpen(true));

    // Cleanup on unmount
    return () => {
      setOnMoreClick(undefined);
    };
  }, [setOnMoreClick]);

  const showActionSheetItems: ActionSheetItem[] = [
    {
      key: 'share',
      label: 'Share',
      onClick: () => console.log('Share clicked'),
    },
    {
      key: 'copy-link',
      label: 'Copy Link',
      onClick: () => console.log('Copy Link clicked'),
    },
    {
      key: 'qr-code',
      label: 'QR Code',
      onClick: () => console.log('QR Code clicked'),
    },
    {
      key: 'edit-show',
      label: 'Edit Show',
      onClick: () => console.log('Edit Show clicked'),
    },
    {
      key: 'delete',
      label: 'Delete',
      danger: true,
      onClick: () => console.log('Delete clicked'),
    },
  ];

  // #region agent log (alert icon sizing)
  useEffect(() => {
    const runId = 'alert-icon-pre';
    try {
      const alerts = Array.from(document.querySelectorAll('.spec-alert-demo'));
      fetch('http://127.0.0.1:7242/ingest/9cae20d4-5a38-488b-b358-edf6af7b3bc9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: 'debug-session',
          runId,
          hypothesisId: 'A',
          location: 'src/pages/system-test.jsx:SystemTest.useEffect(alert-icon)',
          message: 'Alert demo mount: token + alert count',
          data: {
            tokenFontSize: token?.fontSize,
            tokenFontSizeLG: token?.fontSizeLG,
            tokenLineHeight: token?.lineHeight,
            tokenLineHeightLG: token?.lineHeightLG,
            tokenControlOutline: token?.controlOutline,
            alertsFound: alerts.length,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => { });

      alerts.slice(0, 4).forEach((el, idx) => {
        const iconWrap = el.querySelector('.ant-alert-icon');
        const svg = iconWrap?.querySelector('svg') || el.querySelector('svg');
        const iconRect = iconWrap?.getBoundingClientRect?.();
        const svgRect = svg?.getBoundingClientRect?.();
        const iconCS = iconWrap ? window.getComputedStyle(iconWrap) : null;
        const svgCS = svg ? window.getComputedStyle(svg) : null;

        fetch('http://127.0.0.1:7242/ingest/9cae20d4-5a38-488b-b358-edf6af7b3bc9', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: 'debug-session',
            runId,
            hypothesisId: 'B',
            location: 'src/pages/system-test.jsx:SystemTest.useEffect(alert-icon)',
            message: 'Alert icon computed sizing',
            data: {
              idx,
              alertClass: el.className,
              iconWrapExists: Boolean(iconWrap),
              svgExists: Boolean(svg),
              iconWrapFontSize: iconCS?.fontSize,
              iconWrapLineHeight: iconCS?.lineHeight,
              iconWrapDisplay: iconCS?.display,
              iconWrapPosition: iconCS?.position,
              iconWrapFlex: iconCS?.flex,
              iconWrapAlignSelf: iconCS?.alignSelf,
              iconWrapPlaceSelf: iconCS?.placeSelf,
              iconWrapMinWidth: iconCS?.minWidth,
              iconWrapMinHeight: iconCS?.minHeight,
              iconWrapMaxWidth: iconCS?.maxWidth,
              iconWrapMaxHeight: iconCS?.maxHeight,
              iconWrapWidth: iconCS?.width,
              iconWrapHeight: iconCS?.height,
              iconWrapRect: iconRect ? { w: Math.round(iconRect.width), h: Math.round(iconRect.height) } : null,
              svgWidth: svgCS?.width,
              svgHeight: svgCS?.height,
              svgDisplay: svgCS?.display,
              svgPosition: svgCS?.position,
              svgMinWidth: svgCS?.minWidth,
              svgMinHeight: svgCS?.minHeight,
              svgMaxWidth: svgCS?.maxWidth,
              svgMaxHeight: svgCS?.maxHeight,
              svgRect: svgRect ? { w: Math.round(svgRect.width), h: Math.round(svgRect.height) } : null,
              svgAttrWidth: svg?.getAttribute?.('width'),
              svgAttrHeight: svg?.getAttribute?.('height'),
              svgViewBox: svg?.getAttribute?.('viewBox'),
              svgAttrStyle: svg?.getAttribute?.('style'),
              svgInlineStyleWidth: svg?.style?.width,
              svgInlineStyleHeight: svg?.style?.height,
            },
            timestamp: Date.now(),
          }),
        }).catch(() => { });
      });
    } catch (e) {
      fetch('http://127.0.0.1:7242/ingest/9cae20d4-5a38-488b-b358-edf6af7b3bc9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: 'debug-session',
          runId,
          hypothesisId: 'C',
          location: 'src/pages/system-test.jsx:SystemTest.useEffect(alert-icon)',
          message: 'Alert icon sizing probe threw',
          data: { error: String(e) },
          timestamp: Date.now(),
        }),
      }).catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // #endregion agent log (alert icon sizing)

  const pxLineHeight = (fontSize: number, lineHeight: number) => Math.round(fontSize * lineHeight);

  // Table data
  const tableColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Age', dataIndex: 'age', key: 'age' },
    { title: 'Address', dataIndex: 'address', key: 'address' },
  ];

  const tableData = [
    { key: '1', name: 'John Brown', age: 32, address: 'New York No. 1' },
    { key: '2', name: 'Jim Green', age: 42, address: 'London No. 1' },
    { key: '3', name: 'Joe Black', age: 32, address: 'Sidney No. 1' },
  ];

  const tabItems = [
    { key: '1', label: 'Active Tab', children: 'Content 1' },
    { key: '2', label: 'Inactive Tab', children: 'Content 2' },
    { key: '3', label: 'Disabled Tab', disabled: true, children: 'Content 3' },
  ];

  return (
    <div style={{
      padding: spacing.px20,
      color: token.colorText,
      background: token.colorBgLayout,
      minHeight: '100vh',
      maxWidth: '100vw',
      overflowX: 'hidden',
    }}>
      <div className="body-temp" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <h1 style={{
          fontSize: token.fontSizeHeading1,
          lineHeight: token.lineHeightHeading1,
          marginBottom: spacing.px20,
          color: token.colorText
        }}>
          System Test Page
        </h1>

        {/* Header Configuration */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            Header Configuration
          </h2>
          <HeaderConfigDemo />
        </section>

        {/* Auth State Toggle */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            Sidebar Auth State
          </h2>
          <Space>
            <Checkbox
              checked={isAuthenticated}
              onChange={(e) => setIsAuthenticated(e.target.checked)}
            >
              Logged In
            </Checkbox>
            <span style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
              Open the sidebar to see the difference
            </span>
          </Space>
        </section>

        {/* Play Button */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            Play Button
          </h2>
          <PlayButtonDemo />
        </section>

        {/* 1. Overlays */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            1. Overlays
          </h2>
          <Space wrap>
            <Button type="primary" onClick={() => setModalOpen(true)}>
              Modal
            </Button>
            <Button type="primary" onClick={() => setDrawerOpen(true)}>
              Drawer
            </Button>
            <Button type="primary" onClick={() => setDrawerNoOverlayOpen(true)}>
              Drawer — No Overlay
            </Button>
            <Button type="primary" onClick={() => setPersistentDrawerOpen(true)}>
              Persistent Drawer (Map Mode)
            </Button>
          </Space>

          <SpecModal
            title="Modal Title"
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            onOk={() => setModalOpen(false)}
          >
            <p>Modal content - check backdrop opacity, focus ring, and elevation</p>
          </SpecModal>

          <SpecDrawer
            title="Drawer Title"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          >
            <p>Drawer content - check backdrop opacity, focus ring, and elevation</p>
          </SpecDrawer>

          <SpecDrawer
            title="Drawer (No Overlay)"
            open={drawerNoOverlayOpen}
            onClose={() => setDrawerNoOverlayOpen(false)}
            overlay={false}
          >
            <p>This drawer has no background mask. You can interact with the page behind it (if not blocked by other things).</p>
          </SpecDrawer>



          <SpecDrawer
            title="Persistent Drawer"
            open={persistentDrawerOpen}
            onClose={() => setPersistentDrawerOpen(false)}
            overlay={false}
            closeOnClickOutside={false}
            height={drawerHeight}
            extra={
              <Button size="small" onClick={() => setDrawerHeight(h => h === 200 ? 500 : 200)}>
                Toggle Height
              </Button>
            }
          >
            <p>This drawer stays open even if you click the background.</p>
            <p>Current height: {drawerHeight}px</p>
          </SpecDrawer>
        </section>

        {/* 2. Bottom Sheets */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            2. Bottom Sheets
          </h2>

          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px16 }}>
              SpecBottomSheet - Base component for all bottom sheets:
            </div>

            <Space wrap>
              <Button type="primary" onClick={() => setCustomBottomSheetOpen(true)}>
                Basic Bottom Sheet
              </Button>
              <Button type="primary" onClick={() => setCustomBottomSheetWithTitleOpen(true)}>
                With Title
              </Button>
              <Button type="primary" onClick={() => setCustomBottomSheetNoHandleOpen(true)}>
                No Handle
              </Button>
            </Space>
          </div>

          {/* Basic Bottom Sheet */}
          <SpecBottomSheet
            open={customBottomSheetOpen}
            onClose={() => setCustomBottomSheetOpen(false)}
          >
            <div style={{ padding: '0 20px 20px' }}>
              <h3 style={{ color: token.colorText, marginBottom: spacing.px16 }}>
                Custom Content
              </h3>
              <p style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                This is a custom bottom sheet with any content you want.
              </p>
              <p style={{ color: token.colorTextSecondary, marginBottom: spacing.px20 }}>
                You can drag it down or click the backdrop to close.
              </p>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" block onClick={() => setCustomBottomSheetOpen(false)}>
                  Close
                </Button>
                <Button block onClick={() => alert('Action clicked!')}>
                  Some Action
                </Button>
              </Space>
            </div>
          </SpecBottomSheet>

          {/* With Title */}
          <SpecBottomSheet
            open={customBottomSheetWithTitleOpen}
            onClose={() => setCustomBottomSheetWithTitleOpen(false)}
            title="Settings"
          >
            <div style={{ padding: '0 20px 20px' }}>
              <Space direction="vertical" style={{ width: '100%', gap: spacing.px16 }}>
                <div>
                  <h4 style={{ color: token.colorText, marginBottom: spacing.px8 }}>Notifications</h4>
                  <p style={{ color: token.colorTextSecondary, fontSize: 14 }}>
                    Enable push notifications for upcoming shows
                  </p>
                </div>
                <div>
                  <h4 style={{ color: token.colorText, marginBottom: spacing.px8 }}>Location</h4>
                  <p style={{ color: token.colorTextSecondary, fontSize: 14 }}>
                    Share your location to find nearby venues
                  </p>
                </div>
                <Button type="primary" block onClick={() => setCustomBottomSheetWithTitleOpen(false)}>
                  Done
                </Button>
              </Space>
            </div>
          </SpecBottomSheet>

          {/* No Handle */}
          <SpecBottomSheet
            open={customBottomSheetNoHandleOpen}
            onClose={() => setCustomBottomSheetNoHandleOpen(false)}
            showHandle={false}
          >
            <div style={{ padding: '20px' }}>
              <h3 style={{ color: token.colorText, marginBottom: spacing.px16 }}>
                No Drag Handle
              </h3>
              <p style={{ color: token.colorTextSecondary, marginBottom: spacing.px20 }}>
                This bottom sheet has no drag handle. You can still close it by clicking the backdrop.
              </p>
              <Button type="primary" block onClick={() => setCustomBottomSheetNoHandleOpen(false)}>
                Close
              </Button>
            </div>
          </SpecBottomSheet>
        </section>

        {/* 3. Action Sheets */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            3. Action Sheets
          </h2>

          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px16 }}>
              SpecActionSheet - Built on SpecBottomSheet:
            </div>

            <Space wrap>
              <Button type="primary" onClick={() => setShowActionSheetOpen(true)}>
                Show — Options Menu
              </Button>
            </Space>
          </div>

          <SpecActionSheet
            open={showActionSheetOpen}
            onClose={() => setShowActionSheetOpen(false)}
            items={showActionSheetItems}
          />
        </section>

        {/* 4. Typography */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            4. Typography
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.px16 }}>
            <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12 }}>
              <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div style={{ fontSize: token.fontSizeHeading1, lineHeight: token.lineHeightHeading1, fontWeight: 700, color: token.colorText }}>
                  H1 Profile Name
                </div>
                <div style={{ fontSize: token.fontSizeHeading1, lineHeight: token.lineHeightHeading1, fontWeight: 700, color: token.colorBorderSecondary }}>
                  {token.fontSizeHeading1}/{pxLineHeight(token.fontSizeHeading1, token.lineHeightHeading1)} Bold
                </div>
              </div>
              <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeHeading1, lineHeight: token.lineHeightHeading1, fontWeight: 700 }}>
                abcdefghijklmnopqrstuvwxyz
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12 }}>
              <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div style={{ fontSize: token.fontSizeHeading2, lineHeight: token.lineHeightHeading2, fontWeight: 600, color: token.colorText }}>
                  H2 Secondary Titles
                </div>
                <div style={{ fontSize: token.fontSizeHeading2, lineHeight: token.lineHeightHeading2, fontWeight: 600, color: token.colorBorderSecondary }}>
                  {token.fontSizeHeading2}/{pxLineHeight(token.fontSizeHeading2, token.lineHeightHeading2)} Semi Bold
                </div>
              </div>
              <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeHeading2, lineHeight: token.lineHeightHeading2, fontWeight: 600 }}>
                abcdefghijklmnopqrstuvwxyz
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12 }}>
              <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div style={{ fontSize: token.fontSizeHeading3, lineHeight: token.lineHeightHeading3, fontWeight: 700, color: token.colorText }}>
                  H3 Big Tags + Tabs + Primary Card Titles
                </div>
                <div style={{ fontSize: token.fontSizeHeading3, lineHeight: token.lineHeightHeading3, fontWeight: 700, color: token.colorBorderSecondary }}>
                  {token.fontSizeHeading3}/{pxLineHeight(token.fontSizeHeading3, token.lineHeightHeading3)} Bold
                </div>
              </div>
              <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeHeading3, lineHeight: token.lineHeightHeading3, fontWeight: 700 }}>
                abcdefghijklmnopqrstuvwxyz
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.px16, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: token.fontSizeHeading4, lineHeight: token.lineHeightHeading4, fontWeight: 600, color: token.colorText }}>
                      H4 Section + Secondary Card Titles + Buttons
                    </div>
                    <div style={{ fontSize: token.fontSizeHeading4, lineHeight: token.lineHeightHeading4, fontWeight: 600, color: token.colorBorderSecondary }}>
                      {token.fontSizeHeading4}/{pxLineHeight(token.fontSizeHeading4, token.lineHeightHeading4)} Semi Bold
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeHeading4, lineHeight: token.lineHeightHeading4, fontWeight: 600 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: token.fontSizeLG, lineHeight: token.lineHeightLG, fontWeight: 400, color: token.colorText }}>
                      Body Large
                    </div>
                    <div style={{ fontSize: token.fontSizeLG, lineHeight: token.lineHeightLG, fontWeight: 400, color: token.colorBorderSecondary }}>
                      {token.fontSizeLG}/{pxLineHeight(token.fontSizeLG, token.lineHeightLG)} Regular
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeLG, lineHeight: token.lineHeightLG, fontWeight: 400 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.px16, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: token.fontSizeHeading5, lineHeight: token.lineHeightHeading5, fontWeight: 700, color: token.colorText }}>
                      H5 Section Subtitles
                    </div>
                    <div style={{ fontSize: token.fontSizeHeading5, lineHeight: token.lineHeightHeading5, fontWeight: 700, color: token.colorBorderSecondary }}>
                      {token.fontSizeHeading5}/{pxLineHeight(token.fontSizeHeading5, token.lineHeightHeading5)} Bold
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeHeading5, lineHeight: token.lineHeightHeading5, fontWeight: 700 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: token.fontSize, lineHeight: token.lineHeight, fontWeight: 400, color: token.colorText }}>
                      Body
                    </div>
                    <div style={{ fontSize: token.fontSize, lineHeight: token.lineHeight, fontWeight: 400, color: token.colorBorderSecondary }}>
                      {token.fontSize}/{pxLineHeight(token.fontSize, token.lineHeight)} Regular
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSize, lineHeight: token.lineHeight, fontWeight: 400 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.px16, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: typography.fontSizeHeading6, lineHeight: `${typography.lineHeightHeading6}px`, fontWeight: 600, color: token.colorText }}>
                      H6 Small Tags/Buttons + Names
                    </div>
                    <div style={{ fontSize: typography.fontSizeHeading6, lineHeight: `${typography.lineHeightHeading6}px`, fontWeight: 600, color: token.colorBorderSecondary }}>
                      {typography.fontSizeHeading6}/{typography.lineHeightHeading6} Semi Bold
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: typography.fontSizeHeading6, lineHeight: `${typography.lineHeightHeading6}px`, fontWeight: 600 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, fontWeight: 400, color: token.colorText }}>
                      Body Small
                    </div>
                    <div style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, fontWeight: 400, color: token.colorBorderSecondary }}>
                      {token.fontSizeSM}/{pxLineHeight(token.fontSizeSM, token.lineHeightSM)} Regular
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, fontWeight: 400 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${token.colorBorder}`, borderBottom: `1px solid ${token.colorBorder}`, paddingTop: spacing.px12, paddingBottom: spacing.px12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.px16, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: typography.fontSizeHeading7, lineHeight: `${typography.lineHeightHeading7}px`, fontWeight: 700, color: token.colorText }}>
                      H7 Ex-Small Tags + Metadata
                    </div>
                    <div style={{ fontSize: typography.fontSizeHeading7, lineHeight: `${typography.lineHeightHeading7}px`, fontWeight: 700, color: token.colorBorderSecondary }}>
                      {typography.fontSizeHeading7}/{typography.lineHeightHeading7} Bold
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: typography.fontSizeHeading7, lineHeight: `${typography.lineHeightHeading7}px`, fontWeight: 700 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', gap: spacing.px16, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: typography.fontSizeHeading7, lineHeight: `${typography.lineHeightHeading7}px`, fontWeight: 400, color: token.colorText }}>
                      Body Ex-Small
                    </div>
                    <div style={{ fontSize: typography.fontSizeHeading7, lineHeight: `${typography.lineHeightHeading7}px`, fontWeight: 400, color: token.colorBorderSecondary }}>
                      {typography.fontSizeHeading7}/{typography.lineHeightHeading7} Regular
                    </div>
                  </div>
                  <div style={{ marginTop: spacing.px8, color: colors.translucentLightGrey, fontSize: typography.fontSizeHeading7, lineHeight: `${typography.lineHeightHeading7}px`, fontWeight: 400 }}>
                    abcdefghijklmnopqrstuvwxyz
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Buttons */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            6. Buttons
          </h2>
          <Space direction="vertical" size={spacing.px16}>
            <div style={{ color: token.colorTextSecondary }}>Floating Primary (positioned Button)</div>
            <div style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
              Demo: a regular primary button “floated” (positioned) with elevation, without using Ant FloatButton.
            </div>
            <div
              style={{
                position: 'relative',
                height: 120,
                maxWidth: 520,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: spacing.px16, color: token.colorTextSecondary }}>
                Floating container (relative)
              </div>

              <FloatingPrimaryButton
                icon={<ChatIcon size="m" tone="black" />}
                // Use absolute positioning for the demo container (keeps the page usable)
                style={{ position: 'absolute', right: spacing.px16, bottom: spacing.px16, width: 'auto' }}
              >
                Add
              </FloatingPrimaryButton>
            </div>
            <Space wrap>
              <Button type="primary" size="small">Primary Small</Button>
              <Button
                type="primary"
                size="small"
                icon={<HomeIcon size="xs" tone="black" />}
              >
                Primary + Icon
              </Button>
              <Button type="primary" size="small" disabled>Primary Disabled</Button>
            </Space>
            <Space wrap>
              <Button type="primary" size="middle">Primary Medium</Button>
              <Button
                type="primary"
                size="middle"
                icon={<HomeIcon size="m" tone="black" />}
              >
                Primary + Icon
              </Button>
              <Button type="primary" size="middle" disabled>Primary Disabled</Button>
            </Space>
            <Space wrap>
              <Button type="primary" size="large">Primary Large</Button>
              <Button
                type="primary"
                size="large"
                icon={<HomeIcon size="m" tone="black" />}
              >
                Primary + Icon
              </Button>
              <Button type="primary" size="large" disabled>Primary Disabled</Button>
            </Space>
            <Space wrap>
              <Button size="small">Default Small</Button>
              <Button>Default Medium</Button>
              <Button size="large">Default Large</Button>
              <Button
                size="large"
                icon={<HomeIcon size="m" tone="white" />}
              >
                Default + Icon
              </Button>
              <Button size="large" disabled>Default Large Disabled</Button>
            </Space>
            <Space wrap>
              <Button type="text">Text Button</Button>
              <Button type="text" disabled>Text Disabled</Button>
            </Space>
          </Space>
        </section>

        {/* 6b. Accent Buttons (Blue) */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            6b. Accent Buttons (Blue)
          </h2>
          <Space direction="vertical" size={spacing.px16}>
            <div style={{ color: token.colorTextSecondary }}>
              AccentButton — iOS-style blue (#0A84FF) with white text
            </div>
            <Space wrap>
              <AccentButton size="small">Small</AccentButton>
              <AccentButton size="small" icon={<HomeIcon size="xs" tone="white" />}>Small + Icon</AccentButton>
              <AccentButton size="small" disabled>Small Disabled</AccentButton>
            </Space>
            <Space wrap>
              <AccentButton size="middle">Medium</AccentButton>
              <AccentButton size="middle" icon={<HomeIcon size="m" tone="white" />}>Medium + Icon</AccentButton>
              <AccentButton size="middle" disabled>Medium Disabled</AccentButton>
            </Space>
            <Space wrap>
              <AccentButton size="large">Large</AccentButton>
              <AccentButton size="large" icon={<HomeIcon size="m" tone="white" />}>Large + Icon</AccentButton>
              <AccentButton size="large" disabled>Large Disabled</AccentButton>
            </Space>
            <Space wrap>
              <AccentButton size="large" shape="round">Large Round</AccentButton>
              <AccentButton size="large" shape="round" icon={<HomeIcon size="m" tone="white" />}>Round + Icon</AccentButton>
            </Space>
            <div style={{ maxWidth: 320 }}>
              <AccentButton block size="large" shape="round">Block (Full Width)</AccentButton>
            </div>
          </Space>
        </section>

        {/* 6b2. Outline Buttons (White Border) */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            6b2. Outline Buttons (White Border)
          </h2>
          <Space direction="vertical" size={spacing.px16}>
            <div style={{ color: token.colorTextSecondary }}>
              OutlineButton — Transparent background with white border
            </div>
            <Space wrap>
              <OutlineButton size="small">Small</OutlineButton>
              <OutlineButton size="small" icon={<HomeIcon size="xs" tone="white" />}>Small + Icon</OutlineButton>
              <OutlineButton size="small" disabled>Small Disabled</OutlineButton>
            </Space>
            <Space wrap>
              <OutlineButton size="middle">Medium</OutlineButton>
              <OutlineButton size="middle" icon={<HomeIcon size="m" tone="white" />}>Medium + Icon</OutlineButton>
              <OutlineButton size="middle" disabled>Medium Disabled</OutlineButton>
            </Space>
            <Space wrap>
              <OutlineButton size="large">Large</OutlineButton>
              <OutlineButton size="large" icon={<HomeIcon size="m" tone="white" />}>Large + Icon</OutlineButton>
              <OutlineButton size="large" disabled>Large Disabled</OutlineButton>
            </Space>
            <Space wrap>
              <OutlineButton size="large" shape="round">Large Round</OutlineButton>
              <OutlineButton size="large" shape="round" icon={<HomeIcon size="m" tone="white" />}>Round + Icon</OutlineButton>
            </Space>
            <div style={{ maxWidth: 320 }}>
              <OutlineButton block size="large" shape="round">Block (Full Width)</OutlineButton>
            </div>
          </Space>
        </section>

        {/* 6c. Icon Buttons (Circular) */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            6c. Icon Buttons (Circular)
          </h2>
          <Space direction="vertical" size={spacing.px16}>
            <div style={{ color: token.colorTextSecondary }}>
              IconButton — 32×32 perfectly circular icon-only button
            </div>
            <Space wrap size="middle">
              <IconButton icon={<MenuIcon size="m" tone="white" />} aria-label="Menu" />
              <IconButton icon={<XIcon size="m" tone="white" />} aria-label="Close" />
              <IconButton icon={<ChevronLeftIcon size="m" tone="white" />} aria-label="Back" />
              <IconButton icon={<DotsVerticalIcon size="m" tone="white" />} aria-label="More" />
              <IconButton icon={<SearchIcon size="m" tone="white" />} aria-label="Search" />
              <IconButton icon={<HomeIcon size="m" tone="white" />} aria-label="Home" />
              <IconButton icon={<ChatIcon size="m" tone="white" />} aria-label="Chat" />
              <IconButton icon={<CameraIcon size="m" tone="white" />} aria-label="Camera" />
            </Space>
            <Space wrap size="middle">
              <IconButton icon={<MenuIcon size="m" tone="white" />} disabled aria-label="Disabled" />
              <IconButton icon={<XIcon size="m" tone="white" />} disabled aria-label="Disabled" />
            </Space>
          </Space>
        </section>

        {/* 7. Tabs */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            7. Tabs
          </h2>
          <Space direction="vertical" size={spacing.px16} style={{ width: '100%' }}>
            <div>
              <div style={{ marginBottom: spacing.px8, color: token.colorTextSecondary }}>
                Tabs — Small
              </div>
              <Tabs size="small" items={tabItems} defaultActiveKey="1" />
            </div>
            <div>
              <div style={{ marginBottom: spacing.px8, color: token.colorTextSecondary }}>
                Tabs — Default
              </div>
              <Tabs size="middle" items={tabItems} defaultActiveKey="1" />
            </div>
            <div>
              <div style={{ marginBottom: spacing.px8, color: token.colorTextSecondary }}>
                Tabs — Large
              </div>
              <Tabs size="large" items={tabItems} defaultActiveKey="1" />
            </div>
          </Space>

          {/* Show Page Tabs — 50% width with icons */}
          <div style={{ marginTop: spacing.px24 }}>
            <div style={{ marginBottom: spacing.px8, color: token.colorTextSecondary }}>
              Tabs — Show Page (50% width, centered, with icons)
            </div>
            <div className="show-page__tabs-section">
              <Tabs
                defaultActiveKey="comments"
                centered
                items={[
                  {
                    key: 'comments',
                    label: (
                      <span className="show-page__tab-label">
                        <ChatIcon size="s" tone="inherit" />
                        Comments
                      </span>
                    ),
                    children: <div style={{ padding: '24px 0', color: token.colorTextSecondary }}>Comments content here</div>,
                  },
                  {
                    key: 'photos',
                    label: (
                      <span className="show-page__tab-label">
                        <CameraIcon size="s" tone="inherit" />
                        Photos
                      </span>
                    ),
                    children: <div style={{ padding: '24px 0', color: token.colorTextSecondary }}>Photos content here</div>,
                  },
                ]}
              />
            </div>
          </div>
        </section>

        {/* 8. Inputs */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            8. Inputs
          </h2>
          <Space direction="vertical" size={spacing.px16} style={{ width: '100%', maxWidth: 400 }}>
            <Space direction="vertical" size={spacing.px16} style={{ width: '100%', marginBottom: spacing.px16 }}>
              <Input placeholder="Default" />
              <Input size="large" placeholder="Large" />
            </Space>
            <Input defaultValue="Input with value" />
            <Input placeholder="Disabled input" disabled />
            <Input
              prefix={<SearchIcon size="m" tone="white" />}
              placeholder="Input with prefix icon"
            />
            <Input
              suffix={<UserIcon size="m" tone="white" />}
              placeholder="Input with suffix icon"
            />
            <Input.TextArea
              placeholder="Resizable TextArea (square corners)"
              defaultValue="Live music, tasty brunch, mimosas, and good vibes! Enjoy a relaxing Sunday morni..."
              styles={{
                root: { borderRadius: token.borderRadiusSM },
                textarea: {
                  borderRadius: token.borderRadiusSM,
                  resize: 'vertical',
                  overflow: 'auto',
                },
              }}
              rows={4}
            />
          </Space>
        </section>

        {/* 9. Selects */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            9. Selects
          </h2>
          <Space direction="vertical" size={spacing.px16} style={{ width: '100%', maxWidth: 400 }}>
            <Select placeholder="Closed select" style={{ width: '100%' }}>
              <Option value="option1">Option 1</Option>
              <Option value="option2">Option 2</Option>
            </Select>
            {/* Note: don't use the controlled `open` prop here — it forces the dropdown to stay open. */}
            <Select defaultValue="option1" style={{ width: '100%' }}>
              <Option value="option1">Option 1</Option>
              <Option value="option2">Option 2</Option>
            </Select>
            <Select placeholder="Disabled select" disabled style={{ width: '100%' }}>
              <Option value="option1">Option 1</Option>
              <Option value="option2">Option 2</Option>
            </Select>
          </Space>

          {/* Selection controls (Checkbox / Radio) — visual reference */}
          <div style={{ marginTop: spacing.px20 }}>
            <div style={{ display: 'flex', gap: spacing.px24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.px16 }}>
                <Checkbox />
                <Radio />
                <Checkbox />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.px16 }}>
                <Checkbox indeterminate />
                <Radio defaultChecked />
                <Checkbox defaultChecked />
              </div>
            </div>
          </div>
        </section>

        {/* 10. Table */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            10. Table
          </h2>
          <Space direction="vertical" size={spacing.px16} style={{ width: '100%' }}>
            <Table
              columns={tableColumns}
              dataSource={tableData}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }}
              pagination={false}
            />
            <div>
              <h3 style={{
                fontSize: token.fontSizeHeading4,
                marginBottom: spacing.px12,
                color: token.colorText
              }}>
                Empty Table
              </h3>
              <Table
                columns={tableColumns}
                dataSource={[]}
                pagination={false}
              />
            </div>
          </Space>
        </section>

        {/* 11. Empty Text */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            11. Empty Text
          </h2>
          <div style={{ color: token.colorTextTertiary }}>
            No items yet
          </div>
        </section>

        {/* 12. Shadows + Radius */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            12. Shadows + Radius
          </h2>

          <Space direction="vertical" size={spacing.px16} style={{ width: '100%' }}>
            {/* Material (M3) Shadows — Light */}
            <div style={{ color: token.colorTextSecondary }}>Material Shadows — Light</div>
            <div style={{ background: '#fff', borderRadius: token.borderRadius, padding: spacing.px20 }}>
              <div style={{ display: 'flex', gap: spacing.px20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={`m3l-${n}`} style={{ textAlign: 'center' }}>
                    <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>{n}</div>
                    <div style={{ width: 48, height: 48, background: '#fff', borderRadius: radius.s, boxShadow: shadows[`m3Light${n}`] }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Material (M3) Shadows — Dark */}
            <div style={{ color: token.colorTextSecondary }}>Material Shadows — Dark</div>
            <div style={{ background: token.colorBgContainer, borderRadius: token.borderRadius, padding: spacing.px20 }}>
              <div style={{ display: 'flex', gap: spacing.px20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={`m3d-${n}`} style={{ textAlign: 'center' }}>
                    <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>{n}</div>
                    <div style={{ width: 48, height: 48, background: '#000', borderRadius: radius.s, boxShadow: shadows[`m3Dark${n}`] }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Spec Shadows — Light */}
            <div style={{ color: token.colorTextSecondary }}>Spec Shadows — Light</div>
            <div style={{ background: '#fff', borderRadius: token.borderRadius, padding: spacing.px20 }}>
              <div style={{ display: 'flex', gap: spacing.px20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={`spl-${n}`} style={{ textAlign: 'center' }}>
                    <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>{n}</div>
                    <div style={{ width: 48, height: 48, background: '#fff', borderRadius: radius.s, boxShadow: shadows[`specLight${n}`] }} />
                  </div>
                ))}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>Inner</div>
                  <div style={{ width: 48, height: 48, background: '#fff', borderRadius: radius.s, boxShadow: shadows.specLightInner }} />
                </div>
              </div>
            </div>

            {/* Spec Shadows — Dark */}
            <div style={{ color: token.colorTextSecondary }}>Spec Shadows — Dark</div>
            <div style={{ background: token.colorBgContainer, borderRadius: token.borderRadius, padding: spacing.px20 }}>
              <div style={{ display: 'flex', gap: spacing.px20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={`spd-${n}`} style={{ textAlign: 'center' }}>
                    <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>{n}</div>
                    <div style={{ width: 48, height: 48, background: '#000', borderRadius: radius.s, boxShadow: shadows[`specDark${n}`] }} />
                  </div>
                ))}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>Inner</div>
                  <div style={{ width: 48, height: 48, background: '#000', borderRadius: radius.s, boxShadow: shadows.specDarkInner }} />
                </div>
              </div>
            </div>

            {/* Radius */}
            <div style={{ color: token.colorTextSecondary }}>Radius — Rounded Corners</div>
            <div style={{ background: '#fff', borderRadius: token.borderRadius, padding: spacing.px20 }}>
              <div style={{ display: 'flex', gap: spacing.px20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {[
                  { k: 's', label: 'radius-s', v: radius.s },
                  { k: 'm', label: 'radius-m', v: radius.m },
                  { k: 'l', label: 'radius-l', v: radius.l },
                  { k: 'xl', label: 'radius-xl', v: radius.xl },
                  { k: 'xxl', label: 'radius-2xl', v: radius.xxl },
                  { k: 'round', label: 'radius-round', v: radius.round },
                ].map((r) => (
                  <div key={`rad-${r.k}`} style={{ textAlign: 'center' }}>
                    <div style={{ color: token.colorBorderSecondary, fontSize: token.fontSizeSM, marginBottom: spacing.px8 }}>{r.label}</div>
                    <div style={{ width: 48, height: 48, background: '#fff', borderRadius: r.v, boxShadow: shadows.specLight1 }} />
                  </div>
                ))}
              </div>
            </div>
          </Space>
        </section>



        {/* 13. Heroicons */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            13. Heroicons
          </h2>

          <div
            style={{
              background: 'transparent',
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: spacing.px24,
            }}
          >
            {(() => {
              const icons = Object.entries(SolidIcons)
                .filter(([name]) => name.endsWith('Icon'))
                .map(([name, Raw]) => {
                  const Comp = Raw && typeof Raw === 'object' && 'default' in (Raw as any) ? (Raw as any).default : Raw;
                  return [name, Comp];
                })
                // Heroicons exports are often `forwardRef(...)`, which can be `typeof === "object"`.
                .filter(([, Comp]) => Boolean(Comp));

              return (
                <>
                  <div style={{ marginBottom: spacing.px16, color: token.colorBorderSecondary }}>
                    {icons.length} icons
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(36px, 1fr))',
                      gap: spacing.px16,
                      alignItems: 'center',
                    }}
                  >
                    {icons.map(([name, IconComp]) => (
                      <div
                        key={name}
                        title={name}
                        style={{
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: token.colorText,
                        }}
                      >
                        <IconComp style={{ width: 18, height: 18 }} />
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </section>

        {/* 14. Colors */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            14. Colors
          </h2>

          <div
            style={{
              background: token.colorBgBase,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: spacing.px24,
            }}
          >
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px16 }}>
              Basics + Brand
            </div>

            {(() => {
              const groups = [
                {
                  title: 'Black & White',
                  items: [
                    { name: 'black', value: colors.black },
                    { name: 'white', value: colors.white },
                  ],
                },
                {
                  title: 'Greys',
                  items: [
                    { name: 'almost-black', value: colors.almostBlack },
                    { name: 'dark-grey', value: colors.darkGrey },
                    { name: 'light-grey', value: colors.lightGrey },
                    { name: 'translucent-black', value: colors.translucentBlack },
                    { name: 'translucent-light-grey', value: colors.translucentLightGrey },
                  ],
                },
                {
                  title: 'Interface Greys',
                  items: [
                    { name: 'ui-grey-1', value: colors.uiGrey1 },
                    { name: 'ui-grey-2', value: colors.uiGrey2 },
                    { name: 'ui-grey-3', value: colors.uiGrey3 },
                    { name: 'ui-grey-4', value: colors.uiGrey4 },
                  ],
                },
                {
                  title: 'Accents',
                  items: [
                    { name: 'accent-blue', value: colors.accentBlue },
                    { name: 'accent-yellow', value: colors.accentYellow },
                    { name: 'accent-red', value: colors.accentRed },
                  ],
                },
                {
                  title: 'Freshly Squeezed',
                  items: [
                    { name: 'freshly-squeezed-1', value: colors.freshlySqueezed1 },
                    { name: 'freshly-squeezed-2', value: colors.freshlySqueezed2 },
                    { name: 'freshly-squeezed-3', value: colors.freshlySqueezed3 },
                    { name: 'freshly-squeezed-4', value: colors.freshlySqueezed4 },
                  ],
                },
                {
                  title: 'UI',
                  items: [],
                },
                {
                  title: 'Red',
                  items: [
                    { name: 'red-semi', value: colors.redSemi },
                    { name: 'red-2', value: colors.red2 },
                    { name: 'red-3', value: colors.red3 },
                  ],
                },
                {
                  title: 'Yellow',
                  items: [
                    { name: 'yellow-semi', value: colors.yellowSemi },
                    { name: 'yellow-2', value: colors.yellow2 },
                    { name: 'yellow-3', value: colors.yellow3 },
                  ],
                },
                {
                  title: 'Orange',
                  items: [
                    { name: 'orange-semi', value: colors.orangeSemi },
                    { name: 'orange-2', value: colors.orange2 },
                    { name: 'orange-3', value: colors.orange3 },
                  ],
                },
                {
                  title: 'Green',
                  items: [
                    { name: 'green-semi', value: colors.greenSemi },
                    { name: 'green-2', value: colors.green2 },
                    { name: 'green-3', value: colors.green3 },
                  ],
                },
                {
                  title: 'Blue',
                  items: [
                    { name: 'blue-semi', value: colors.blueSemi },
                    { name: 'blue-2', value: colors.blue2 },
                    { name: 'blue-3', value: colors.blue3 },
                  ],
                },
              ];

              const Swatch = ({ name, value }: { name: string, value: string }) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.px12,
                    minWidth: 120,
                    maxWidth: 180,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: token.borderRadiusSM,
                      background: value,
                      boxShadow: shadows.specLight1,
                      border: `1px solid ${token.colorBorderSecondary}`,
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      lineHeight: token.lineHeight,
                      minWidth: 0,
                    }}
                  >
                    <div style={{ color: token.colorText, fontWeight: 700 }}>{name}</div>
                    <div
                      style={{
                        color: token.colorTextSecondary,
                        fontSize: token.fontSizeSM,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {value}
                    </div>
                  </div>
                </div>
              );

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.px24 }}>
                  {groups.map((g) => (
                    <div
                      key={g.title}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: spacing.px12,
                      }}
                    >
                      <div style={{ color: token.colorText, fontWeight: 700 }}>
                        {g.title}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: spacing.px16,
                        }}
                      >
                        {g.items.map((it) => (
                          <Swatch key={it.name} name={it.name} value={it.value} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>

        {/* 15. Alerts */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2
            style={{
              fontSize: token.fontSizeHeading3,
              lineHeight: token.lineHeightHeading3,
              marginBottom: spacing.px16,
              color: token.colorText,
            }}
          >
            15. Alerts
          </h2>

          {(() => {
            const Label = ({ children }: { children: React.ReactNode }) => (
              <div style={{ fontSize: token.fontSizeSM, lineHeight: token.lineHeightSM, color: token.colorTextSecondary }}>
                {children}
              </div>
            );

            return (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Label>Default (message-only)</Label>
                <Alert
                  className="spec-alert-demo"
                  message="Info"
                  type="info"
                  showIcon
                  icon={<SolidIcons.InformationCircleIcon style={{ width: 20, height: 20 }} />}
                />

                <Label>Default + action slot</Label>
                <Alert
                  className="spec-alert-demo"
                  message="With action"
                  type="info"
                  showIcon
                  icon={<SolidIcons.InformationCircleIcon style={{ width: 20, height: 20 }} />}
                  action={
                    <Button size="small" type="primary">
                      Action
                    </Button>
                  }
                />
              </Space>
            );
          })()}
        </section>

        {/* 16. User Tags */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            16. User Tags
          </h2>

          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px16 }}>
              Click user tags to see account type information:
            </div>

            <Space wrap size="middle">
              <UserTag type="musician" onClick={() => setMusicianInfoOpen(true)} />
              <UserTag type="fan" onClick={() => setFanInfoOpen(true)} />
              <UserTag type="band" onClick={() => setBandInfoOpen(true)} />
              <UserTag type="venue" onClick={() => setVenueInfoOpen(true)} />
              <UserTag type="showContributor" onClick={() => setShowContributorInfoOpen(true)} />
              <UserTag type="bandMember" onClick={() => setBandMemberInfoOpen(true)} />
              <UserTag type="noAccount" onClick={() => setNoAccountInfoOpen(true)} />
            </Space>
          </div>

          {/* User Type Info Bottom Sheets */}
          <UserTypeInfo
            type="musician"
            open={musicianInfoOpen}
            onClose={() => setMusicianInfoOpen(false)}
          />
          <UserTypeInfo
            type="fan"
            open={fanInfoOpen}
            onClose={() => setFanInfoOpen(false)}
          />
          <UserTypeInfo
            type="band"
            open={bandInfoOpen}
            onClose={() => setBandInfoOpen(false)}
          />
          <UserTypeInfo
            type="venue"
            open={venueInfoOpen}
            onClose={() => setVenueInfoOpen(false)}
          />
          <UserTypeInfo
            type="showContributor"
            open={showContributorInfoOpen}
            onClose={() => setShowContributorInfoOpen(false)}
          />
          <UserTypeInfo
            type="bandMember"
            open={bandMemberInfoOpen}
            onClose={() => setBandMemberInfoOpen(false)}
          />
          <UserTypeInfo
            type="noAccount"
            open={noAccountInfoOpen}
            onClose={() => setNoAccountInfoOpen(false)}
          />
        </section>

        {/* 17. Directory Listings */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            17. Directory Listings
          </h2>

          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px16 }}>
              Search result listing components for Shows, Bands, Musicians, and Venues:
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.px16
            }}>
              <ShowListing
                bandName="Johnny Mullenax Band"
                withMembers={[
                  { name: "Thomas Trap", avatarUrl: "https://i.pravatar.cc/150?img=1" },
                  { name: "Andrew Bair", avatarUrl: "https://i.pravatar.cc/150?img=2" },
                  { name: "David Mullenax" }
                ]}
                imageUrl="/images/bands/johnny-mullenax.png"
                date="Wed Sep 17"
                description="Live music, tasty brunch, mimosas, and good vibes! Enjoy a relaxing Sunday morning with gre..."
                genres={['Psychedelic', 'Bluegrass', 'Rock', 'Funk']}
                venue="Mercury Lounge"
                time="6:00 PM"
                creator="Johnny Mullenax"
                onClick={() => console.log('Show clicked')}
              />

              <ShowListing
                bandName="Johnny Murrell"
                imageUrl="/images/bands/johnny-murrell.png"
                date="Thu Sep 18"
                genres={['Country', 'Americana']}
                venue="Cain's Ballroom"
                time="8:30 PM"
                creator="Johnny Murrell"
                onClick={() => console.log('Show clicked')}
              />

              <ShowListing
                bandName="Earl Hazard"
                date="Fri Sep 19"
                description="Intimate acoustic performance on the rooftop terrace"
                genres={['Jazz', 'Blues', 'Soul']}
                venue="The Vanguard"
                time="7:00 PM"
                creator="Earl Hazard"
                onClick={() => console.log('Show clicked')}
              />

              <ShowListing
                bandName="SOUP"
                imageUrl="/images/bands/soup.png"
                date="Sat Sep 20"
                genres={['Funk', 'Jam', 'Rock', 'Psychedelic', 'Soul']}
                venue="Soundpony"
                time="10:00 PM"
                creator="SOUP"
                onClick={() => console.log('Show clicked')}
              />

              <BandListing
                name="Johnny Mullenax Band"
                imageUrl="/images/bands/johnny-mullenax.png"
                onClick={() => console.log('Band clicked')}
              />
              <BandListing
                name="Johnny Murrell"
                imageUrl="/images/bands/johnny-murrell.png"
                onClick={() => console.log('Band clicked')}
              />
              <BandListing
                name="SOUP"
                imageUrl="/images/bands/soup.png"
                onClick={() => console.log('Band clicked')}
              />

              <VenueListing
                name="Venue Listing"
                location="Tulsa, OK"
                onClick={() => console.log('Venue clicked')}
              />

              <MusicianListing
                name="Musician Listing"
                onClick={() => console.log('Musician clicked')}
              />
              <MusicianListing
                name="Johnny Murrell"
                imageUrl="https://cdn.prod.website-files.com/632688ceec38a92558da32b0/666767741d5e72bf793f52db_448240121_18434515315016948_9059941516875307154_n%20copy.jpg"
                onClick={() => console.log('Musician clicked')}
              />

              <div style={{ marginTop: spacing.px16, color: '#ABABAB', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                <SolidIcons.SparklesIcon style={{ width: 14, height: 14 }} />
                <span>Directory Item — No result</span>
              </div>

              <AddListing
                title="Add Show"
                subtitle="Add your show"
                onClick={() => console.log('Add Show clicked')}
              />
              <AddListing
                title="Add Musician"
                subtitle="Send invite"
                onClick={() => console.log('Add Musician clicked')}
              />
              <AddListing
                title="Add Band"
                subtitle="Add your band"
                onClick={() => console.log('Add Band clicked')}
              />
              <AddListing
                title="Add Venue"
                subtitle="Contact us"
                onClick={() => console.log('Add Venue clicked')}
              />
            </div>
          </div>
        </section>

        {/* 18. Photo Gallery */}
        <section style={{ marginBottom: spacing.px24 }}>
          <h2 style={{
            fontSize: token.fontSizeHeading3,
            lineHeight: token.lineHeightHeading3,
            marginBottom: spacing.px16,
            color: token.colorText
          }}>
            18. Photo Gallery
          </h2>

          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            {/* PhotoRibbon Demo */}
            <div style={{ marginBottom: spacing.px24 }}>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                PhotoRibbon — Horizontal scrolling row (up to 10 photos)
              </div>
              <PhotoRibbon 
                photos={samplePhotos} 
                maxPhotos={10}
                thumbnailSize={88}
              />
            </div>

            {/* PhotoGrid Demo */}
            <div style={{ marginBottom: spacing.px24 }}>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                PhotoGrid — 3x3 grid with "View All" button
              </div>
              <PhotoGrid 
                photos={samplePhotos}
                title="Show Photos"
                onViewAll={() => setPhotoGalleryWithTabsOpen(true)}
              />
            </div>

            {/* PhotoGalleryDrawer Triggers */}
            <div>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                PhotoGalleryDrawer — Full gallery in drawer
              </div>
              <Space wrap>
                <Button type="primary" onClick={() => setPhotoGalleryDrawerOpen(true)}>
                  Open Gallery Drawer
                </Button>
                <Button type="primary" onClick={() => setPhotoGalleryWithTabsOpen(true)}>
                  Gallery with Tabs
                </Button>
              </Space>
            </div>
          </div>

          {/* PhotoGalleryDrawer - Basic */}
          <PhotoGalleryDrawer
            open={photoGalleryDrawerOpen}
            onClose={() => setPhotoGalleryDrawerOpen(false)}
            photos={samplePhotos}
            title="Show Photos"
          />

          {/* PhotoGalleryDrawer - With Tabs */}
          <PhotoGalleryDrawer
            open={photoGalleryWithTabsOpen}
            onClose={() => setPhotoGalleryWithTabsOpen(false)}
            photos={samplePhotos}
            title="Show Photos"
            showTabs
            upcomingPhotos={samplePhotos.slice(0, 6)}
            pastPhotos={samplePhotos.slice(6)}
          />
        </section>

        {/* Tag Components Demo */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>19. Tag Components</h2>
          
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            {/* TagRow Wrap Mode */}
            <div style={{ marginBottom: spacing.px24 }}>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                TagRow (wrap mode) — Tags wrap to next line
              </div>
              <TagRow mode="wrap">
                <Tag>Psychedlic</Tag>
                <Tag>Bluegrass</Tag>
                <Tag>Rock</Tag>
                <Tag>Funk</Tag>
                <Tag>Country</Tag>
                <Tag>Hip Hop</Tag>
                <Tag>Blues</Tag>
                <Tag>Red Dirt</Tag>
              </TagRow>
            </div>

            {/* TagRow Ribbon Mode */}
            <div style={{ marginBottom: spacing.px24 }}>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                TagRow (ribbon mode) — Horizontal scroll
              </div>
              <TagRow mode="ribbon">
                <Tag>Guitarist</Tag>
                <Tag>Singer</Tag>
                <Tag>Drummer</Tag>
                <Tag>Bassist</Tag>
                <Tag>Keyboardist</Tag>
                <Tag>Violinist</Tag>
                <Tag>Saxophonist</Tag>
                <Tag>Trumpeter</Tag>
              </TagRow>
            </div>

            {/* Clickable Tags */}
            <div>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                Clickable Tags — With onClick handler
              </div>
              <TagRow mode="wrap">
                <Tag onClick={() => alert('Rock clicked!')}>Rock</Tag>
                <Tag onClick={() => alert('Jazz clicked!')}>Jazz</Tag>
                <Tag onClick={() => alert('Blues clicked!')}>Blues</Tag>
              </TagRow>
            </div>
          </div>
        </section>

        {/* Comment Component Demo */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>20. Comment Component</h2>
          
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            {/* Full Comment with Event Info */}
            <div style={{ marginBottom: spacing.px24 }}>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                Show Comment — Full (with event info row)
              </div>
              <Comment
                userName="Jane Smith"
                timestamp="Sept 16 3:29 PM"
                userTags={['fan', 'showContributor', 'bandMember']}
                text="TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three"
                showEventInfo={true}
                eventInfo={{
                  artistName: "Johnny Mullen...",
                  artistAvatarUrl: "https://cdn.prod.website-files.com/632688ceec38a92558da32b0/666767741d5e72bf793f52db_448240121_18434515315016948_9059941516875307154_n%20copy.jpg",
                  date: "Wed Sep 17",
                  venue: "Mercury Lounge",
                }}
                onTagClick={(type) => console.log('Tag clicked:', type)}
              />
            </div>

            {/* Simple Comment without Event Info */}
            <div style={{ marginBottom: spacing.px24 }}>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                Show Comment — Simple (no event info)
              </div>
              <Comment
                userName="Jane Smith"
                timestamp="Sept 16 3:29 PM"
                userTags={['fan', 'showContributor']}
                text="TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three"
                showEventInfo={false}
              />
            </div>

            {/* Musician Comment */}
            <div>
              <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
                Musician Comment
              </div>
              <Comment
                userName="Johnny Mullenax"
                avatarUrl="https://cdn.prod.website-files.com/632688ceec38a92558da32b0/666767741d5e72bf793f52db_448240121_18434515315016948_9059941516875307154_n%20copy.jpg"
                timestamp="Sept 16 3:29 PM"
                userTags={['musician', 'bandMember']}
                text="TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three"
              />
            </div>
          </div>
        </section>

        {/* CommentList Component Demo */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>21. CommentList Component</h2>
          
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              CommentList — Shows first 3 comments with "View All" button
            </div>
            <CommentList
              comments={[
                {
                  userName: 'Jane Smith',
                  timestamp: 'Sept 16 3:29 PM',
                  userTags: [],
                  text: 'three',
                },
                {
                  userName: 'George Smith',
                  avatarUrl: 'https://i.pravatar.cc/150?img=8',
                  timestamp: 'Sept 16 3:29 PM',
                  userTags: ['fan', 'showContributor'],
                  text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
                },
                {
                  userName: 'Mike Johnson',
                  avatarUrl: 'https://i.pravatar.cc/150?img=12',
                  timestamp: 'Sept 15 8:45 PM',
                  userTags: ['musician'],
                  text: 'Can\'t wait to see you guys play! The last show was incredible.',
                },
                {
                  userName: 'Sarah Wilson',
                  timestamp: 'Sept 15 6:30 PM',
                  userTags: ['fan'],
                  text: 'Is there a cover charge for this show?',
                },
                {
                  userName: 'Tom Davis',
                  avatarUrl: 'https://i.pravatar.cc/150?img=15',
                  timestamp: 'Sept 14 2:15 PM',
                  userTags: ['showContributor'],
                  text: 'Great venue choice! The sound system there is amazing.',
                },
              ]}
              maxComments={3}
              onViewAll={() => setCommentDrawerOpen(true)}
            />
          </div>
        </section>

        {/* CommentDrawer */}
        <CommentDrawer
          open={commentDrawerOpen}
          onClose={() => setCommentDrawerOpen(false)}
          comments={[
            {
              userName: 'Jane Smith',
              timestamp: 'Sept 16 3:29 PM',
              userTags: [],
              text: 'three',
            },
            {
              userName: 'George Smith',
              avatarUrl: 'https://i.pravatar.cc/150?img=8',
              timestamp: 'Sept 16 3:29 PM',
              userTags: ['fan', 'showContributor'],
              text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
            },
            {
              userName: 'Mike Johnson',
              avatarUrl: 'https://i.pravatar.cc/150?img=12',
              timestamp: 'Sept 15 8:45 PM',
              userTags: ['musician'],
              text: 'Can\'t wait to see you guys play! The last show was incredible.',
            },
            {
              userName: 'Sarah Wilson',
              timestamp: 'Sept 15 6:30 PM',
              userTags: ['fan'],
              text: 'Is there a cover charge for this show?',
            },
            {
              userName: 'Tom Davis',
              avatarUrl: 'https://i.pravatar.cc/150?img=15',
              timestamp: 'Sept 14 2:15 PM',
              userTags: ['showContributor'],
              text: 'Great venue choice! The sound system there is amazing.',
            },
          ]}
          title="Comments"
          fullscreen
        />

        {/* 22. SpecAlert */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>22. SpecAlert</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              SpecAlert — Floating alert notification using Ant Alert (appears near bottom of viewport)
            </div>
            <Space wrap>
              <Button type="primary" onClick={() => setToastMessage('Link copied!')}>
                Show Alert — "Link copied!"
              </Button>
              <Button type="primary" onClick={() => setToastMessage('Invite Sent')}>
                Show Alert — "Invite Sent"
              </Button>
              <Button type="primary" onClick={() => setToastMessage('Photo uploaded successfully')}>
                Show Alert — "Photo uploaded"
              </Button>
            </Space>
          </div>
        </section>

        {/* 23. AccentButtonDark + BrandedButton */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>23. AccentButtonDark + BrandedButton</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              AccentButtonDark — Dark variant for secondary actions (sidebar, calendar sheet)
            </div>
            <Space wrap style={{ marginBottom: spacing.px20 }}>
              <AccentButtonDark size="small">Small</AccentButtonDark>
              <AccentButtonDark size="middle">Medium</AccentButtonDark>
              <AccentButtonDark size="large">Large</AccentButtonDark>
              <AccentButtonDark size="large" shape="round">Round</AccentButtonDark>
              <AccentButtonDark size="large" disabled>Disabled</AccentButtonDark>
            </Space>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              BrandedButton — SVG-based branded accent button
            </div>
            <div style={{ maxWidth: 320 }}>
              <BrandedButton onClick={() => console.log('Branded button clicked')} />
            </div>
          </div>
        </section>

        {/* 24. MarqueeText */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>24. MarqueeText</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              MarqueeText — Static when text fits; continuous scroll when it overflows
            </div>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <div style={{ color: '#ABABAB', fontSize: 12, marginBottom: 4 }}>Fits container (no scroll)</div>
                <div style={{ maxWidth: 300, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: '8px 12px' }}>
                  <MarqueeText>
                    <span style={{ color: token.colorText, fontWeight: 600 }}>Short Band Name</span>
                  </MarqueeText>
                </div>
              </div>

              <div>
                <div style={{ color: '#ABABAB', fontSize: 12, marginBottom: 4 }}>Overflows container (marquee scroll)</div>
                <div style={{ maxWidth: 200, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: '8px 12px' }}>
                  <MarqueeText>
                    <span style={{ color: token.colorText, fontWeight: 600 }}>Johnny Mullenax Band Live at the Mercury Lounge</span>
                  </MarqueeText>
                </div>
              </div>
            </Space>
          </div>
        </section>

        {/* 25. DateSubheader */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>25. DateSubheader</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              DateSubheader — Date navigation bar with pill labels and nav arrows
            </div>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <DateSubheader
                dateLabel="Wed Sep 17"
                pillLabel="Today"
                pillColor="red"
                timeLabel="6:00 PM"
                onPrevious={() => console.log('Previous')}
                onNext={() => console.log('Next')}
                onDateClick={() => console.log('Date clicked')}
              />
              <DateSubheader
                dateLabel="Thu Sep 18"
                pillLabel="Tomorrow"
                pillColor="grey"
                timeLabel="8:30 PM"
                onPrevious={() => console.log('Previous')}
                onNext={() => console.log('Next')}
              />
              <DateSubheader
                dateLabel="Fri Sep 19"
                pillLabel="This Week"
                pillColor="grey"
                showNavArrows={false}
                dateClickable={false}
              />
              <div style={{ color: '#ABABAB', fontSize: 12, marginTop: 8 }}>Pill color variants:</div>
              <Space wrap>
                {(['red', 'grey'] as const).map((color) => (
                  <DateSubheader
                    key={color}
                    dateLabel={color}
                    pillLabel={color}
                    pillColor={color}
                    showNavArrows={false}
                    dateClickable={false}
                  />
                ))}
              </Space>
            </Space>
          </div>
        </section>

        {/* 26. Show Page Tabs (Comments / Photos) */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>26. Show Page Tabs (Comments / Photos)</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              Ant Tabs — Show Page style (50% width, centered, with icons)
            </div>
            <div className="show-page__tabs-section">
              <Tabs
                defaultActiveKey="comments"
                centered
                items={[
                  {
                    key: 'comments',
                    label: (
                      <span className="show-page__tab-label">
                        <ChatIcon size="s" tone="inherit" />
                        Comments
                      </span>
                    ),
                    children: <div style={{ padding: '24px 0', color: token.colorTextSecondary }}>Comments content here</div>,
                  },
                  {
                    key: 'photos',
                    label: (
                      <span className="show-page__tab-label">
                        <CameraIcon size="s" tone="inherit" />
                        Photos
                      </span>
                    ),
                    children: <div style={{ padding: '24px 0', color: token.colorTextSecondary }}>Photos content here</div>,
                  },
                ]}
              />
            </div>
          </div>
        </section>

        {/* 27. DirectorySearch */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>27. DirectorySearch</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              DirectorySearch — Search bar with category tabs (inline mode)
            </div>
            <DirectorySearch
              activeTab="shows"
              onTabChange={(key) => console.log('Tab changed:', key)}
            />
          </div>
        </section>

        {/* 28. CommentReplyBar + AddCommentDrawer */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>28. CommentReplyBar + AddCommentDrawer</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              CommentReplyBar — Tappable bar that opens the add-comment flow
            </div>
            <div style={{ maxWidth: 400, marginBottom: spacing.px16 }}>
              <CommentReplyBar
                onClick={() => setAddCommentDrawerOpen(true)}
                onEmojiClick={(emoji) => console.log('Quick emoji:', emoji)}
              />
            </div>
            <Button type="primary" onClick={() => setAddCommentDrawerOpen(true)}>
              Open AddCommentDrawer
            </Button>
          </div>
        </section>

        <AddCommentDrawer
          open={addCommentDrawerOpen}
          onClose={() => setAddCommentDrawerOpen(false)}
          onSubmit={(comment) => {
            console.log('Comment submitted:', comment);
            setAddCommentDrawerOpen(false);
            setToastMessage('Comment posted!');
          }}
          showContext={sampleShowContext}
          isLoggedIn={false}
        />

        {/* 29. Cards — ShowPhotoCard, BandInfoLockup, VenueCard, PoweredBySpec */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>29. Cards + Lockups</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              ShowPhotoCard — Hero card with date header, band info, and action buttons
            </div>
            <div style={{ maxWidth: 400, marginBottom: spacing.px24 }}>
              <ShowPhotoCard
                bandName="Johnny Mullenax Band"
                imageUrl="/images/bands/johnny-mullenax.png"
                bandMembers={[
                  { name: 'Thomas Trap', avatarUrl: 'https://i.pravatar.cc/150?img=1' },
                  { name: 'Andrew Bair', avatarUrl: 'https://i.pravatar.cc/150?img=2' },
                  { name: 'David Mullenax' },
                ]}
                dateLabel="Wed Sep 17"
                timeLabel="6:00 PM"
                pillLabel="Today"
                pillColor="red"
                onAddPhoto={() => console.log('Add photo')}
                onCopyLink={() => {
                  navigator.clipboard?.writeText(window.location.href).catch(() => {});
                  setToastMessage('Link copied!');
                }}
              />
            </div>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              BandInfoLockup — Band avatar + name + "With" member row
            </div>
            <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 400, marginBottom: spacing.px24 }}>
              <BandInfoLockup
                bandName="Johnny Mullenax Band"
                imageUrl="/images/bands/johnny-mullenax.png"
                bandMembers={[
                  { name: 'Thomas Trap', avatarUrl: 'https://i.pravatar.cc/150?img=1' },
                  { name: 'Andrew Bair' },
                ]}
                onClick={() => console.log('Band clicked')}
              />
              <BandInfoLockup
                bandName="SOUP"
                imageUrl="/images/bands/soup.png"
                onClick={() => console.log('Band clicked')}
              />
            </Space>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              VenueCard — Location icon + venue name lockup
            </div>
            <div style={{ maxWidth: 400, marginBottom: spacing.px24 }}>
              <VenueCard
                venueName="Mercury Lounge"
                location="Tulsa, OK"
                onClick={() => console.log('Venue clicked')}
              />
            </div>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              PoweredBySpec — Branding lockup
            </div>
            <PoweredBySpec />
          </div>
        </section>

        {/* 30. PhotoOverlayInfo + PhotoActionBar + AddPhotoDrawer */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>30. Photo Overlay + Action Bar + AddPhotoDrawer</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              PhotoOverlayInfo — User + event info overlay for fullscreen photo lightbox
            </div>
            <div style={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              height: 280,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundImage: 'url(https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=400&fit=crop)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              marginBottom: spacing.px16,
            }}>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 12 }}>
                <PhotoOverlayInfo
                  showUserInfo
                  showEventInfo
                  metadata={{
                    userName: 'Jane Smith',
                    userAvatarUrl: 'https://i.pravatar.cc/150?img=5',
                    timestamp: 'Sept 16 3:29 PM',
                    userTags: ['fan', 'showContributor'],
                    userProfileUrl: '/fan-profile',
                    eventInfo: {
                      artistName: 'Johnny Mullenax Band',
                      artistAvatarUrl: '/images/bands/johnny-mullenax.png',
                      date: 'Wed Sep 17',
                      venue: 'Mercury Lounge',
                    },
                  }}
                />
              </div>
            </div>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              PhotoThumbnailInfo — Small avatar + name overlay for thumbnails
            </div>
            <div style={{
              position: 'relative',
              width: 120,
              height: 120,
              borderRadius: 8,
              overflow: 'hidden',
              backgroundImage: 'url(https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=200&h=200&fit=crop)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              marginBottom: spacing.px16,
            }}>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                <PhotoThumbnailInfo
                  metadata={{
                    userName: 'Jane Smith',
                    userAvatarUrl: 'https://i.pravatar.cc/150?img=5',
                    timestamp: 'Sept 16',
                    userTags: ['fan'],
                    userProfileUrl: '/fan-profile',
                  }}
                />
              </div>
            </div>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              PhotoActionBar — Tappable bar for add-photo flow
            </div>
            <div style={{ maxWidth: 400, marginBottom: spacing.px16 }}>
              <PhotoActionBar onClick={() => setAddPhotoDrawerOpen(true)} />
            </div>
            <Button type="primary" onClick={() => setAddPhotoDrawerOpen(true)}>
              Open AddPhotoDrawer
            </Button>
          </div>
        </section>

        <AddPhotoDrawer
          open={addPhotoDrawerOpen}
          onClose={() => setAddPhotoDrawerOpen(false)}
          onSubmit={(photo) => {
            console.log('Photo submitted:', photo);
            setAddPhotoDrawerOpen(false);
            setToastMessage('Photo uploaded!');
          }}
          showContext={sampleShowContext}
          isLoggedIn={false}
          selectedFile={null}
          previewUrl={null}
        />

        {/* 31. AddToCalendarSheet + CalendarOverlay */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>31. AddToCalendarSheet + CalendarOverlay</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              AddToCalendarSheet — Bottom sheet with Google / Apple / Outlook calendar options
            </div>
            <Space wrap style={{ marginBottom: spacing.px16 }}>
              <Button type="primary" onClick={() => setCalendarSheetOpen(true)}>
                Open AddToCalendarSheet
              </Button>
            </Space>

            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              CalendarOverlay — Full-screen date picker overlay
            </div>
            <Button type="primary" onClick={() => setCalendarOverlayOpen(true)}>
              Open CalendarOverlay
            </Button>
          </div>
        </section>

        <AddToCalendarSheet
          open={calendarSheetOpen}
          onClose={() => setCalendarSheetOpen(false)}
          event={{
            title: 'Johnny Mullenax Band at Mercury Lounge',
            description: 'Live music, tasty brunch, mimosas, and good vibes!',
            location: 'Mercury Lounge, Tulsa, OK',
            startDate: '2025-09-17T18:00:00',
          }}
        />

        <CalendarOverlay
          open={calendarOverlayOpen}
          onClose={() => setCalendarOverlayOpen(false)}
          onSelect={(date) => {
            console.log('Date selected:', date.format('YYYY-MM-DD'));
            setCalendarOverlayOpen(false);
          }}
        />

        {/* 32. ManageDrawer — ShowContributors + BandMembers */}
        <section style={{ marginBottom: spacing.px32 }}>
          <h2 style={{ color: token.colorText, marginBottom: spacing.px16 }}>32. ManageDrawers</h2>
          <div style={{
            background: token.colorBgBase,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            padding: spacing.px24,
          }}>
            <div style={{ color: token.colorTextSecondary, marginBottom: spacing.px12 }}>
              ShowContributorsDrawer + BandMembersDrawer — Full management flows with invite, roles, and removal
            </div>
            <Space wrap>
              <Button type="primary" onClick={() => setShowContributorsOpen(true)}>
                Show Contributors Drawer
              </Button>
              <Button type="primary" onClick={() => setBandMembersOpen(true)}>
                Band Members Drawer
              </Button>
            </Space>
          </div>
        </section>

        <ShowContributorsDrawer
          open={showContributorsOpen}
          onClose={() => setShowContributorsOpen(false)}
          bandName="Johnny Mullenax Band"
        />

        <BandMembersDrawer
          open={bandMembersOpen}
          onClose={() => setBandMembersOpen(false)}
          bandName="Johnny Mullenax Band"
        />

        {/* Global Alert */}
        <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />

      </div>
    </div>
  );
}

export default SystemTest;
